import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IBuyerOrderLine, IBuyerOrderLineRequest } from '../../interfaces/garments/buyerOrder.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import StyleAutocomplete from './StyleAutocomplete';
import { formatMoney } from '../../utils/format';

interface EditableLine {
    key: string;
    style: IStyle | null;
    colorName: string;
    sizeName: string;
    quantity: string;
    unitPrice: string;
}

const toEditable = (line: IBuyerOrderLine, styleLookup: Record<string, IStyle>, index: number): EditableLine => ({
    key: line.id || `existing-${index}`,
    style: styleLookup[line.styleId] ?? null,
    colorName: line.colorName ?? '',
    sizeName: line.sizeName ?? '',
    quantity: line.quantity != null ? String(line.quantity) : '',
    unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
});

const blankLine = (key: string): EditableLine => ({
    key, style: null, colorName: '', sizeName: '', quantity: '', unitPrice: '',
});

/** Client-side mirror of the server-computed `lineValue` (`quantity * unitPrice`) — used only for
 *  the live total while a row is being edited; the saved total always comes back from the server. */
const lineValue = (line: EditableLine): number => {
    const qty = parseFloat(line.quantity);
    const price = parseFloat(line.unitPrice);
    if (Number.isNaN(qty) || Number.isNaN(price)) return 0;
    return qty * price;
};

interface BuyerOrderLinesEditorProps {
    lines: IBuyerOrderLine[];
    /** styleId -> resolved IStyle, so an existing line's picker shows its real style rather than
     *  an id-only stub — the DTO carries only `styleId` (see `BuyerOrderForm`'s resolution step). */
    styleLookup: Record<string, IStyle>;
    /** Narrow the style picker to the order's buyer, like the sample request line form does. */
    buyerId?: string;
    canManage: boolean;
    /** True once the order has left DRAFT — the grid becomes fully read-only (H4/REV 2). */
    readOnly: boolean;
    /** Shown above the grid whenever `readOnly` is true, explaining why editing is blocked. */
    readOnlyReason?: string;
    saving: boolean;
    /** Server-computed total from the order header — shown once the sheet is not "dirty". */
    savedTotal: number;
    /** Order currency — shown next to the totals when known. */
    currencyCode?: string | null;
    onSave: (requests: IBuyerOrderLineRequest[], usedStyles: IStyle[]) => Promise<void>;
}

let keySeq = 0;
const nextKey = () => `new-line-${++keySeq}`;

/**
 * Repeating line-item grid for a buyer order (garments-merchandising T10): style, colour, size,
 * quantity, unit price, and a live order total. Mirrors `StyleCostSheet`'s replace-all save
 * semantics — Save Lines sends the FULL current row set (`PUT {id}/lines`), which the backend
 * swaps in wholesale. Read-only once the order has left DRAFT; `readOnlyReason` states why.
 */
export function BuyerOrderLinesEditor({
    lines, styleLookup, buyerId, canManage, readOnly, readOnlyReason, saving, savedTotal, currencyCode, onSave,
}: BuyerOrderLinesEditorProps) {
    const [rows, setRows] = useState<EditableLine[]>(() => lines.map((l, i) => toEditable(l, styleLookup, i)));
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setRows(lines.map((l, i) => toEditable(l, styleLookup, i)));
        setDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, styleLookup]);

    const liveTotal = rows.reduce((sum, r) => sum + lineValue(r), 0);
    const canEdit = canManage && !readOnly;

    const updateRow = (key: string, patch: Partial<EditableLine>) => {
        setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
        setDirty(true);
    };

    const addRow = () => {
        setRows(prev => [...prev, blankLine(nextKey())]);
        setDirty(true);
    };

    const removeRow = (key: string) => {
        setRows(prev => prev.filter(r => r.key !== key));
        setDirty(true);
    };

    const handleSave = async () => {
        const usable = rows.filter(r => r.style && r.quantity.trim() !== '' && r.unitPrice.trim() !== '');
        const requests: IBuyerOrderLineRequest[] = usable.map((r, index) => ({
            styleId: r.style!.id,
            colorName: r.colorName.trim() || null,
            sizeName: r.sizeName.trim() || null,
            quantity: parseInt(r.quantity, 10),
            unitPrice: parseFloat(r.unitPrice),
            sortOrder: index,
        }));
        const usedStyles = usable.map(r => r.style!);
        await onSave(requests, usedStyles);
        setDirty(false);
    };

    const saveWhy = saving ? null
        : !dirty ? 'Change a line first, there is nothing to save.'
        : null;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Lines</Typography>
                {canEdit && (
                    <Button data-help="merchandising.orderLines.add" size="small" startIcon={<AddIcon />} onClick={addRow}>
                        Add Line
                    </Button>
                )}
            </Box>
            {readOnly && (
                <Alert severity="info" sx={{ mb: 1 }} data-testid="lines-readonly-reason">
                    {readOnlyReason ?? 'Lines can only be edited while the order is DRAFT.'}
                </Alert>
            )}
            <TableContainer component={Paper} variant="outlined" data-help-section="merchandising.orderLines.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="merchandising.orderLines.col.style">Style</TableCell>
                            <TableCell data-help="merchandising.orderLines.col.colour">Colour</TableCell>
                            <TableCell data-help="merchandising.orderLines.col.size">Size</TableCell>
                            <TableCell align="right" data-help="merchandising.orderLines.col.qty">Qty</TableCell>
                            <TableCell align="right" data-help="merchandising.orderLines.col.unitPrice">Unit Price</TableCell>
                            <TableCell align="right" data-help="merchandising.orderLines.col.lineValue">Line Value</TableCell>
                            {canEdit && <TableCell align="right" data-help="merchandising.orderLines.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={row.key}>
                                <TableCell sx={{ minWidth: 220 }}>
                                    {canEdit ? (
                                        <StyleAutocomplete
                                            data-help="merchandising.orderLines.col.style"
                                            value={row.style}
                                            onChange={style => updateRow(row.key, { style })}
                                            buyerId={buyerId}
                                            activeOnly
                                            size="small"
                                        />
                                    ) : (row.style ? `${row.style.styleNumber} — ${row.style.name}` : '—')}
                                </TableCell>
                                <TableCell sx={{ minWidth: 100 }}>
                                    {canEdit ? (
                                        <TextField
                                            data-help="merchandising.orderLines.col.colour" size="small" fullWidth value={row.colorName}
                                            onChange={e => updateRow(row.key, { colorName: e.target.value })}
                                        />
                                    ) : (row.colorName || '—')}
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    {canEdit ? (
                                        <TextField
                                            data-help="merchandising.orderLines.col.size" size="small" fullWidth value={row.sizeName}
                                            onChange={e => updateRow(row.key, { sizeName: e.target.value })}
                                        />
                                    ) : (row.sizeName || '—')}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    {canEdit ? (
                                        <TextField
                                            data-help="merchandising.orderLines.col.qty" size="small" type="number" value={row.quantity}
                                            onChange={e => updateRow(row.key, { quantity: e.target.value })}
                                        />
                                    ) : (row.quantity || '—')}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 100 }}>
                                    {canEdit ? (
                                        <TextField
                                            data-help="merchandising.orderLines.col.unitPrice" size="small" type="number" value={row.unitPrice}
                                            onChange={e => updateRow(row.key, { unitPrice: e.target.value })}
                                        />
                                    ) : (row.unitPrice || '—')}
                                </TableCell>
                                <TableCell align="right">{formatMoney(lineValue(row))}</TableCell>
                                {canEdit && (
                                    <TableCell align="right">
                                        <IconButton data-help="merchandising.orderLines.remove" size="small" onClick={() => removeRow(row.key)} aria-label="Remove line">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={canEdit ? 7 : 6} align="center">No lines</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2">
                    Total: {formatMoney(liveTotal, currencyCode)}
                    {!dirty && ` (saved: ${formatMoney(savedTotal, currencyCode)})`}
                </Typography>
                {canEdit && (
                    <Button data-help="merchandising.orderLines.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" size="small" onClick={handleSave} disabled={saving || !dirty}>
                        {saving ? 'Saving…' : 'Save Lines'}
                    </Button>
                )}
            </Box>
        </Box>
    );
}

export default BuyerOrderLinesEditor;
